import React from "react";
import { useAnketaI18n } from "src/internationalization";
import { OpsanioIcon } from "src/assets/images/opsanio/opsanio_icon";
import { usePrefersReducedMotion } from "./motion";

/*
 * Hero visual: the "infrastructure aggregation" atom.
 *
 * opsanio's story in one picture - disparate infrastructure sources (Kubernetes,
 * VMs, cloud, identity, endpoints, network, firewalls, SIEM) are continuously
 * pulled into one sovereign core. Data visibly FLOWS inward along the connectors
 * (aggregation); electrons glide along tilted orbit rings for the "atom" feel
 * (à la XM Cyber's hero). The core is the real opsanio logo.
 *
 * Smoothness: the orbit rings stay still (a rotated bitmap of a 1px stroke
 * shimmers); only the electrons move, via SMIL animateMotion along the exact
 * ellipse, and the flow dash period divides the keyframe offset so every loop
 * is seamless. CSS animations are reduced-motion-gated (classes in
 * homepage-theme.css); the electrons are dropped to static dots via
 * usePrefersReducedMotion, so the diagram renders static when requested.
 *
 * Each source node reveals the systems it stands for on hover (mouse), tap
 * (touch) or focus (keyboard); Escape closes. The names are a subset of the
 * authoritative list in ./integrations - keep the two in sync.
 */

const CX = 240;
const CY = 240;
const R = 168; // source-node ring radius

// 24×24 stroke icons (drawn with currentColor).
const I = {
  k8s: (
    <path
      d="M12 2l8.66 5v10L12 22 3.34 17V7L12 2zM12 7.5v9M7.5 9.75l9 4.5M16.5 9.75l-9 4.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
      strokeLinecap="round"
    />
  ),
  server: (
    <path
      d="M4 4h16v6H4V4zM4 14h16v6H4v-6zM7 7h.01M7 17h.01"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  cloud: (
    <path
      d="M7 18a4 4 0 010-8 5 5 0 019.6-1.5A3.5 3.5 0 0117 18H7z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinejoin="round"
    />
  ),
  key: (
    <path
      d="M15 7a4 4 0 11-3.9 5H8v3H5v-3H3v-2h8.1A4 4 0 0115 7zM16 10h.01"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  endpoint: (
    <path
      d="M3 5h18v11H3V5zM8 20h8M12 16v4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  siem: (
    <path
      d="M3 12h4l2 6 4-14 2 8h6"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  network: (
    <path
      d="M6 7a2 2 0 100-.01M18 7a2 2 0 100-.01M12 19a2 2 0 100-.01M6.8 8.2l4 8M17.2 8.2l-4 8M6.5 6.8h11"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
  firewall: (
    <path
      d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3zM9.5 11.5l2 2 3.5-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  ),
};

const polar = (angleDeg: number, radius: number) => {
  const a = (angleDeg * Math.PI) / 180;
  return { x: CX + radius * Math.cos(a), y: CY + radius * Math.sin(a) };
};

// Orbits: [tiltDeg, rx, ry, secondsPerRevolution]. The rings themselves stay
// still (crisp strokes); only the electrons travel along them.
const ORBITS: Array<[number, number, number, number]> = [
  [0, 186, 64, 11],
  [60, 186, 64, 14],
  [120, 186, 64, 17],
];

// Closed ellipse around the centre, starting at its right-hand vertex.
const orbitPath = (rx: number, ry: number) =>
  `M ${CX + rx} ${CY} A ${rx} ${ry} 0 1 1 ${CX - rx} ${CY} A ${rx} ${ry} 0 1 1 ${CX + rx} ${CY} Z`;

// Node radius as a share of the (square) diagram width - used to offset the
// detail panel from its node in CSS percentages (margins resolve against width).
const NODE_R = 26;
const NODE_R_PCT = ((NODE_R + 8) / 480) * 100;

type Source = {
  key: string;
  angle: number;
  icon: JSX.Element;
  title: string;
  desc: string;
  // Integration names (product terms, not translated) - a subset of the
  // authoritative list in ./integrations.
  items: string[];
};

// Where the detail panel opens relative to its node: towards the centre, so it
// never leaves the hero.
const panelPlacement = (x: number, y: number): React.CSSProperties => {
  const dx = x - CX;
  if (Math.abs(dx) < 1) {
    return y < CY
      ? { transform: "translate(-50%, 0)", marginTop: `${NODE_R_PCT}%` }
      : { transform: "translate(-50%, -100%)", marginTop: `-${NODE_R_PCT}%` };
  }
  return dx > 0
    ? { transform: "translate(-100%, -50%)", marginLeft: `-${NODE_R_PCT}%` }
    : { transform: "translate(0, -50%)", marginLeft: `${NODE_R_PCT}%` };
};

export const AutonubilInfraAggregation: React.FC = () => {
  const { translate } = useAnketaI18n();
  const reduced = usePrefersReducedMotion();
  const [active, setActive] = React.useState<string | null>(null);
  // Mouse opens on hover; touch/pen open on tap (a tap also fires
  // pointerenter, so hover handling is limited to real mice).
  const lastPointer = React.useRef<string>("mouse");
  const tp = "anketa.autonubil.home.hero.atom";
  const t = (key: string, fallback: string) =>
    translate(`${tp}.src.${key}`, { defaultMessage: fallback } as any);

  const sources: Source[] = [
    {
      key: "k8s",
      angle: -90,
      icon: I.k8s,
      title: t("k8s.title", "Kubernetes"),
      desc: t("k8s.desc", "Clusters, workloads and every container image - with SBOM and CVEs."),
      items: ["Kubernetes agent", "Docker", "Podman", "containerd"],
    },
    {
      key: "cloud",
      angle: -45,
      icon: I.cloud,
      title: t("cloud.title", "Cloud & SaaS"),
      desc: t("cloud.desc", "Tenants, managed devices and services from your cloud platforms."),
      items: ["Microsoft Graph", "Office 365", "Microsoft Intune", "HPE GreenLake", "HPE Aruba Central"],
    },
    {
      key: "identity",
      angle: 0,
      icon: I.key,
      title: t("identity.title", "Identity"),
      desc: t("identity.desc", "Directories and identity providers - merged into one identity per person."),
      items: ["Active Directory", "Microsoft Entra ID", "Keycloak"],
    },
    {
      key: "endpoints",
      angle: 45,
      icon: I.endpoint,
      title: t("endpoints.title", "Endpoints"),
      desc: t("endpoints.desc", "Clients and servers from EDR, UEM and the opsanio Agent."),
      items: ["opsanio Agent", "SentinelOne", "Microsoft Defender", "Cortex XDR", "baramundi", "NinjaOne", "Windows (WinRM)"],
    },
    {
      key: "siem",
      angle: 90,
      icon: I.siem,
      title: t("siem.title", "SIEM & security events"),
      desc: t("siem.desc", "Integrated, managed Wazuh SIEM plus the security services you already run."),
      items: ["Wazuh", "Arctic Wolf", "Menlo Security", "Threat Intel Feeds"],
    },
    {
      key: "network",
      angle: 135,
      icon: I.network,
      title: t("network.title", "Network"),
      desc: t("network.desc", "Discovery scans, flow data and your monitoring & documentation tools."),
      items: ["Network Scan", "IPFIX / NetFlow / sFlow", "NetBox", "PRTG", "Zabbix", "Icinga 2", "Checkmk"],
    },
    {
      key: "firewall",
      angle: 180,
      icon: I.firewall,
      title: t("firewall.title", "Firewalls"),
      desc: t("firewall.desc", "Firewalls and security gateways across vendors."),
      items: ["Fortinet", "SonicWall", "Sophos"],
    },
    {
      key: "servers",
      angle: 225,
      icon: I.server,
      title: t("servers.title", "Servers & virtualisation"),
      desc: t("servers.desc", "Hypervisors, VMs, storage and bare metal - down to the BMC."),
      items: ["VMware vCenter", "Proxmox", "Nutanix", "OpenNebula", "Azure Local", "HPE Alletra / Primera / 3PAR", "Redfish (BMC)"],
    },
  ];

  const current = sources.find((s) => s.key === active);
  const currentPos = current ? polar(current.angle, R) : null;

  return (
    <div
      className="ak-fade-up"
      style={{ width: "100%", maxWidth: 560, position: "relative", aspectRatio: "1 / 1" }}
      role="group"
      aria-label={translate(`${tp}.aria`, {
        defaultMessage:
          "Infrastructure sources - Kubernetes, cloud, identity, endpoints, SIEM, network, firewalls and servers - continuously aggregated into the opsanio core",
      } as any)}
      onKeyDown={(e) => {
        if (e.key === "Escape") setActive(null);
      }}
    >
      <svg
        viewBox="0 0 480 480"
        width="100%"
        height="100%"
        style={{ display: "block", overflow: "visible", position: "absolute", inset: 0 }}
      >
        <defs>
          <radialGradient id="aggCoreGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="var(--ak-accent-2)" stopOpacity="0.5" />
            <stop offset="55%" stopColor="var(--ak-accent)" stopOpacity="0.12" />
            <stop offset="100%" stopColor="var(--ak-accent)" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* orbits: still, tilted rings; electrons glide along them (SMIL
            animateMotion follows the ellipse exactly and scales with the
            viewBox). Two electrons per ring, half a revolution apart. */}
        <g aria-hidden="true">
          {ORBITS.map(([tilt, rx, ry, dur]) => {
            const d = orbitPath(rx, ry);
            return (
              <g key={tilt} transform={`rotate(${tilt} ${CX} ${CY})`}>
                <path d={d} fill="none" stroke="var(--ak-on-navy-border)" strokeWidth="1" />
                {[
                  { r: 4, fill: "var(--ak-accent-2)", opacity: 1, phase: 0 },
                  { r: 2.5, fill: "var(--ak-accent)", opacity: 0.8, phase: 0.5 },
                ].map((e) =>
                  reduced ? (
                    <circle
                      key={e.phase}
                      cx={e.phase ? CX - rx : CX + rx}
                      cy={CY}
                      r={e.r}
                      fill={e.fill}
                      opacity={e.opacity}
                    />
                  ) : (
                    <circle key={e.phase} r={e.r} fill={e.fill} opacity={e.opacity}>
                      <animateMotion
                        dur={`${dur}s`}
                        begin={`${-e.phase * dur}s`}
                        repeatCount="indefinite"
                        path={d}
                      />
                    </circle>
                  )
                )}
              </g>
            );
          })}
        </g>

        {/* ambient glow */}
        <circle className="ak-core-glow" cx={CX} cy={CY} r={150} fill="url(#aggCoreGlow)" aria-hidden="true" />

        {/* connectors with data flowing inward (the aggregation). The dash
            period (2 + 12) divides the keyframe offset (28), so the loop is
            seamless. */}
        {sources.map((s, i) => {
          const p = polar(s.angle, R);
          const on = active === s.key;
          return (
            <g key={`line-${s.key}`} aria-hidden="true">
              <line
                x1={p.x}
                y1={p.y}
                x2={CX}
                y2={CY}
                stroke={on ? "var(--ak-accent-border)" : "var(--ak-on-navy-border)"}
                strokeWidth="1"
              />
              <line
                className="ak-flow"
                x1={p.x}
                y1={p.y}
                x2={CX}
                y2={CY}
                stroke="var(--ak-accent-2)"
                strokeWidth={on ? 2.2 : 1.6}
                strokeLinecap="round"
                strokeDasharray="2 12"
                style={{
                  animationDuration: `${2.4 + (i % 4) * 0.4}s`,
                  animationDelay: `${i * 0.16}s`,
                  opacity: on ? 1 : active ? 0.35 : 0.7,
                  transition: "opacity 0.2s ease",
                }}
              />
            </g>
          );
        })}

        {/* core pulse rings */}
        {[0, 1.9].map((delay, i) => (
          <circle
            key={i}
            className="ak-pulse-ring"
            cx={CX}
            cy={CY}
            r={46}
            fill="none"
            stroke="var(--ak-accent-2)"
            strokeWidth="1.5"
            style={{ animationDuration: "3.8s", animationDelay: `${delay}s` }}
            aria-hidden="true"
          />
        ))}

        {/* opsanio core: logo on a dark disc with a teal ring */}
        <g aria-hidden="true">
          <circle cx={CX} cy={CY} r={46} fill="var(--ak-navy-2)" stroke="var(--ak-accent-border)" strokeWidth="1.5" />
          <OpsanioIcon x={CX - 24} y={CY - 28} width={48} height={56} />
        </g>

        {/* source nodes - hover (mouse), tap (touch) or focus (keyboard)
            reveals which systems each one stands for */}
        {sources.map((s) => {
          const p = polar(s.angle, R);
          const on = active === s.key;
          return (
            <g
              key={s.key}
              role="button"
              tabIndex={0}
              aria-label={s.title}
              aria-expanded={on}
              aria-describedby={on ? "ak-atom-detail" : undefined}
              onPointerDown={(e) => {
                lastPointer.current = e.pointerType;
              }}
              onPointerEnter={(e) => {
                if (e.pointerType === "mouse") setActive(s.key);
              }}
              onPointerLeave={(e) => {
                if (e.pointerType === "mouse") setActive((a) => (a === s.key ? null : a));
              }}
              onClick={() => {
                if (lastPointer.current !== "mouse") setActive(s.key);
              }}
              onFocus={() => setActive(s.key)}
              onBlur={() => setActive((a) => (a === s.key ? null : a))}
              style={{
                cursor: "help",
                outline: "none",
                opacity: active && !on ? 0.55 : 1,
                transform: on ? "scale(1.1)" : "none",
                transformBox: "fill-box",
                transformOrigin: "center",
                transition: reduced ? undefined : "transform 0.2s ease, opacity 0.2s ease",
              }}
            >
              {/* halo only while revealed */}
              <circle
                cx={p.x}
                cy={p.y}
                r={NODE_R + 7}
                fill="var(--ak-accent-tint-strong)"
                stroke="var(--ak-accent-2)"
                strokeOpacity={0.35}
                strokeWidth="1"
                opacity={on ? 1 : 0}
                style={{ transition: "opacity 0.2s ease" }}
              />
              <circle
                cx={p.x}
                cy={p.y}
                r={NODE_R}
                fill="var(--ak-navy-2)"
                stroke={on ? "var(--ak-accent-2)" : "var(--ak-accent-border)"}
                strokeWidth={on ? 2 : 1.5}
              />
              <g transform={`translate(${p.x - 12}, ${p.y - 12})`} style={{ color: "var(--ak-accent-2)" }}>
                {s.icon}
              </g>
            </g>
          );
        })}
      </svg>

      {current && currentPos && (
        // Outer box positions (transform); the inner panel fades in - kept
        // apart because the ak-fade keyframes would override the transform.
        <div
          style={{
            position: "absolute",
            left: `${(currentPos.x / 480) * 100}%`,
            top: `${(currentPos.y / 480) * 100}%`,
            ...panelPlacement(currentPos.x, currentPos.y),
            width: "min(260px, 64%)",
            zIndex: 2,
            pointerEvents: "none",
          }}
        >
          <div
            id="ak-atom-detail"
            role="tooltip"
            className="ak-fade"
            style={{
              padding: "14px 16px",
              borderRadius: 12,
              background: "var(--ak-navy-2)",
              border: "1px solid var(--ak-accent-border)",
              boxShadow: "0 16px 40px rgba(0, 0, 0, 0.45)",
              color: "var(--ak-on-navy)",
              textAlign: "left",
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: "-0.01em" }}>{current.title}</div>
            <div
              style={{
                marginTop: 4,
                fontSize: 12.5,
                lineHeight: 1.5,
                color: "var(--ak-on-navy-secondary)",
              }}
            >
              {current.desc}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 10 }}>
              {current.items.map((name) => (
                <span
                  key={name}
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    padding: "3px 8px",
                    borderRadius: 999,
                    color: "var(--ak-accent-2)",
                    background: "var(--ak-on-navy-surface-2)",
                    border: "1px solid var(--ak-on-navy-border)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {name}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutonubilInfraAggregation;
